import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faPen, faCheck, faTimes, faSpinner, faInfoCircle, faExclamationTriangle, faPlus, faUserPlus, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const TicketMobileSubHeader = ({
  ticket,
  onToggleMobileSidebar,
  onTicketUpdated
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [isEditingMotivo, setIsEditingMotivo] = useState(false);
  const [motivoText, setMotivoText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  const ticketId = ticket?.idTicket || ticket?.id;
  const motivoActual = ticket?.vAsunto || ticket?.subject || '';

  const [policiesAlertInfo, setPoliciesAlertInfo] = useState({ isDismissed: false, hasExpired: false, count: 0 });
  const [unassignedClientAlertInfo, setUnassignedClientAlertInfo] = useState({ isDismissed: false, hasNoClient: false });
  const [unassignedLevelAlertInfo, setUnassignedLevelAlertInfo] = useState({ isDismissed: false, hasNoLevel: false });
  const [isAlertsMenuOpen, setIsAlertsMenuOpen] = useState(false);
  const alertsMenuRef = useRef(null);

  useEffect(() => {
    const handleAlertState = e => {
      if (e?.detail) {
        setPoliciesAlertInfo(e.detail);
      }
    };
    window.addEventListener('akasia:support:policies_alert_state', handleAlertState);
    return () => {
      window.removeEventListener('akasia:support:policies_alert_state', handleAlertState);
    };
  }, []);

  useEffect(() => {
    const handleUnassignedState = e => {
      if (e?.detail) {
        setUnassignedClientAlertInfo(e.detail);
      }
    };
    window.addEventListener('akasia:support:unassigned_client_alert_state', handleUnassignedState);
    return () => {
      window.removeEventListener('akasia:support:unassigned_client_alert_state', handleUnassignedState);
    };
  }, []);

  useEffect(() => {
    const handleUnassignedLevelState = e => {
      if (e?.detail) {
        setUnassignedLevelAlertInfo(e.detail);
      }
    };
    window.addEventListener('akasia:support:unassigned_level_alert_state', handleUnassignedLevelState);
    return () => {
      window.removeEventListener('akasia:support:unassigned_level_alert_state', handleUnassignedLevelState);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (alertsMenuRef.current && !alertsMenuRef.current.contains(event.target)) {
        setIsAlertsMenuOpen(false);
      }
    };
    if (isAlertsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isAlertsMenuOpen]);

  useEffect(() => {
    setPoliciesAlertInfo({ isDismissed: false, hasExpired: false, count: 0 });
    setUnassignedClientAlertInfo({ isDismissed: false, hasNoClient: false });
    setUnassignedLevelAlertInfo({ isDismissed: false, hasNoLevel: false });
    setIsAlertsMenuOpen(false);
  }, [ticketId]);

  useEffect(() => {
    if (!isEditingMotivo) {
      setMotivoText(motivoActual);
    }
  }, [motivoActual, ticketId, isEditingMotivo]);

  useEffect(() => {
    if (isEditingMotivo && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingMotivo]);

  const handleStartEdit = () => {
    setMotivoText(motivoActual);
    setIsEditingMotivo(true);
  };

  const handleCancelEdit = () => {
    setMotivoText(motivoActual);
    setIsEditingMotivo(false);
  };

  const handleSaveMotivo = async e => {
    if (e) e.preventDefault();
    if (!ticketId || isSaving) return;

    const trimmed = motivoText.trim();
    if (trimmed === motivoActual) {
      setIsEditingMotivo(false);
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch('/api/SupportEngine/UpdateTicketSubject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId, 10),
          subject: trimmed
        })
      });

      if (response.ok) {
        if (onTicketUpdated) {
          onTicketUpdated({ vAsunto: trimmed });
        }
        setIsEditingMotivo(false);
        if (showSuccessToast) {
          showSuccessToast('Motivo actualizado', 'El motivo del ticket se guardó correctamente.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (showErrorToast) {
          showErrorToast('Error', errorData.message || 'No se pudo actualizar el motivo.');
        }
      }
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error de conexión', err.message || 'Ocurrió un error al guardar.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="d-xl-none px-3 py-2 border-bottom d-flex align-items-center gap-2 flex-shrink-0"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#f8fafc',
        borderColor: isDark ? '#233142' : '#e2e8f0',
        minHeight: '44px'
      }}
    >
      {/* Botón Detalles en píldora con borde azul suave (lado izquierdo) */}
      {onToggleMobileSidebar && (
        <button
          type="button"
          onClick={onToggleMobileSidebar}
          className="btn btn-sm px-3 py-1 rounded-pill d-inline-flex align-items-center gap-2 shadow-none flex-shrink-0"
          style={{
            backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : '#ffffff',
            color: isDark ? '#38bdf8' : '#0284c7',
            border: isDark ? '1px solid rgba(56, 189, 248, 0.35)' : '1px solid #bfdbfe',
            fontSize: '0.80rem',
            fontWeight: 600
          }}
          title="Ver detalles del ticket"
        >
          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.78rem' }} />
          <span>Detalles</span>
        </button>
      )}

      {/* Motivo del ticket (vista compacta / edición al lado de Detalles) */}
      <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
        {isEditingMotivo ? (
          <form onSubmit={handleSaveMotivo} className="d-flex align-items-center gap-1 w-100">
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm rounded-pill px-3"
              placeholder="Motivo del ticket..."
              value={motivoText}
              onChange={e => setMotivoText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancelEdit();
                }
              }}
              disabled={isSaving}
              maxLength={250}
              style={{
                fontSize: '0.80rem',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f8fafc' : '#0f172a',
                borderColor: isDark ? '#3b82f6' : '#93c5fd'
              }}
            />
            <button
              type="submit"
              className="btn btn-sm btn-primary d-flex align-items-center justify-content-center p-0 rounded-circle flex-shrink-0"
              style={{ width: '28px', height: '28px', minWidth: '28px' }}
              title="Guardar motivo"
              disabled={isSaving}
            >
              {isSaving ? (
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.70rem' }} />
              ) : (
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.70rem' }} />
              )}
            </button>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="btn btn-sm d-flex align-items-center justify-content-center p-0 rounded-circle shadow-none flex-shrink-0"
              style={{
                width: '28px',
                height: '28px',
                minWidth: '28px',
                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
                color: isDark ? '#94a3b8' : '#64748b'
              }}
              title="Cancelar"
              disabled={isSaving}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.70rem' }} />
            </button>
          </form>
        ) : (
          <div
            className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill shadow-none"
            style={{
              cursor: 'pointer',
              maxWidth: '100%',
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
              transition: 'all 0.15s ease'
            }}
            onClick={handleStartEdit}
            title="Clic para editar el motivo del ticket"
            role="button"
            tabIndex={0}
          >
            <FontAwesomeIcon
              icon={faTag}
              style={{ fontSize: '0.70rem', color: motivoActual ? '#3b82f6' : (isDark ? '#64748b' : '#94a3b8') }}
            />
            <span
              className={`text-truncate ${motivoActual ? 'fw-medium' : 'fst-italic'}`}
              style={{
                fontSize: '0.80rem',
                color: motivoActual ? (isDark ? '#e2e8f0' : '#475569') : (isDark ? '#94a3b8' : '#64748b'),
                maxWidth: '220px'
              }}
            >
              {motivoActual || '+ Agregar motivo'}
            </span>
            <FontAwesomeIcon
              icon={faPen}
              style={{ fontSize: '0.66rem', color: isDark ? '#94a3b8' : '#64748b', opacity: 0.8 }}
            />
          </div>
        )}
      </div>

      {/* Botón "+" para desplegar Alertas actuales y futuras en móvil */}
      <div className="position-relative flex-shrink-0" ref={alertsMenuRef}>
        <button
          type="button"
          onClick={() => setIsAlertsMenuOpen(prev => !prev)}
          className="btn btn-sm d-flex align-items-center justify-content-center p-0 rounded-circle shadow-none position-relative"
          style={{
            width: '32px',
            height: '32px',
            minWidth: '32px',
            backgroundColor: (policiesAlertInfo.hasExpired || unassignedClientAlertInfo.hasNoClient || unassignedLevelAlertInfo.hasNoLevel)
              ? (policiesAlertInfo.hasExpired
                  ? (isDark ? 'rgba(239, 68, 68, 0.22)' : '#fee2e2')
                  : (unassignedClientAlertInfo.hasNoClient
                      ? (isDark ? 'rgba(245, 158, 11, 0.22)' : '#fef3c7')
                      : (isDark ? 'rgba(99, 102, 241, 0.22)' : '#e0e7ff')))
              : (isDark ? '#1e293b' : '#ffffff'),
            border: (policiesAlertInfo.hasExpired || unassignedClientAlertInfo.hasNoClient || unassignedLevelAlertInfo.hasNoLevel)
              ? (policiesAlertInfo.hasExpired
                  ? (isDark ? '1px solid rgba(239, 68, 68, 0.55)' : '1px solid #f87171')
                  : (unassignedClientAlertInfo.hasNoClient
                      ? (isDark ? '1px solid rgba(245, 158, 11, 0.55)' : '1px solid #f59e0b')
                      : (isDark ? '1px solid rgba(99, 102, 241, 0.55)' : '1px solid #818cf8')))
              : (isDark ? '1px solid #334155' : '1px solid #cbd5e1'),
            color: (policiesAlertInfo.hasExpired || unassignedClientAlertInfo.hasNoClient || unassignedLevelAlertInfo.hasNoLevel)
              ? (policiesAlertInfo.hasExpired
                  ? (isDark ? '#fca5a5' : '#b91c1c')
                  : (unassignedClientAlertInfo.hasNoClient
                      ? (isDark ? '#fbbf24' : '#b45309')
                      : (isDark ? '#c7d2fe' : '#3730a3')))
              : (isDark ? '#94a3b8' : '#475569'),
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          title="Ver alertas del ticket"
          aria-label="Ver alertas del ticket"
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.80rem' }} />
          {(policiesAlertInfo.hasExpired || unassignedClientAlertInfo.hasNoClient || unassignedLevelAlertInfo.hasNoLevel) && (
            <span
              className="position-absolute rounded-circle"
              style={{
                top: '-2px',
                right: '-2px',
                width: '9px',
                height: '9px',
                backgroundColor: policiesAlertInfo.hasExpired
                  ? '#ef4444'
                  : (unassignedClientAlertInfo.hasNoClient ? '#f59e0b' : '#6366f1'),
                border: isDark ? '2px solid #121e2d' : '2px solid #ffffff'
              }}
            />
          )}
        </button>

        {/* Menú flotante de alertas */}
        {isAlertsMenuOpen && (
          <div
            className="position-absolute end-0 shadow-lg rounded-3 py-2 px-1"
            style={{
              top: 'calc(100% + 6px)',
              minWidth: '230px',
              maxWidth: '280px',
              backgroundColor: isDark ? '#1a2736' : '#ffffff',
              border: isDark ? '1px solid #2d3d52' : '1px solid #e2e8f0',
              zIndex: 1060
            }}
          >
            <div
              className="px-2 py-1 mb-1 border-bottom d-flex align-items-center justify-content-between"
              style={{ borderColor: isDark ? '#2d3d52' : '#f1f5f9' }}
            >
              <span
                className="fw-bold"
                style={{
                  fontSize: '0.72rem',
                  color: isDark ? '#94a3b8' : '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em'
                }}
              >
                Alertas del Ticket
              </span>
              {(policiesAlertInfo.hasExpired || unassignedClientAlertInfo.hasNoClient || unassignedLevelAlertInfo.hasNoLevel) && (
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: policiesAlertInfo.hasExpired
                      ? '#ef4444'
                      : (unassignedClientAlertInfo.hasNoClient ? '#f59e0b' : '#6366f1'),
                    color: '#ffffff',
                    fontSize: '0.62rem'
                  }}
                >
                  {(policiesAlertInfo.hasExpired ? 1 : 0) +
                    (unassignedClientAlertInfo.hasNoClient ? 1 : 0) +
                    (unassignedLevelAlertInfo.hasNoLevel ? 1 : 0)}
                </span>
              )}
            </div>

            {/* Alerta de Pólizas Vencidas */}
            {policiesAlertInfo.hasExpired && (
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('akasia:support:reopen_policies_alert'));
                  setIsAlertsMenuOpen(false);
                }}
                className="w-100 text-start btn btn-sm d-flex align-items-center gap-2 px-2 py-2 rounded-2 border-0 shadow-none mb-1"
                style={{
                  backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fff1f2',
                  color: isDark ? '#fca5a5' : '#991b1b',
                  fontSize: '0.78rem'
                }}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444', fontSize: '0.85rem' }} />
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-bold text-truncate">Pólizas Vencidas</div>
                  <div style={{ fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                    {policiesAlertInfo.isDismissed ? 'Toca para abrir alerta' : 'Alerta abierta'}
                  </div>
                </div>
              </button>
            )}

            {/* Alerta de Ticket Sin Cliente Asignado */}
            {unassignedClientAlertInfo.hasNoClient && (
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('akasia:support:reopen_unassigned_client_alert'));
                  setIsAlertsMenuOpen(false);
                }}
                className="w-100 text-start btn btn-sm d-flex align-items-center gap-2 px-2 py-2 rounded-2 border-0 shadow-none mb-1"
                style={{
                  backgroundColor: isDark ? 'rgba(245, 158, 11, 0.16)' : '#fffbeb',
                  color: isDark ? '#fbbf24' : '#b45309',
                  fontSize: '0.78rem'
                }}
              >
                <FontAwesomeIcon icon={faUserPlus} style={{ color: '#f59e0b', fontSize: '0.85rem' }} />
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-bold text-truncate">Asignar Cliente</div>
                  <div style={{ fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                    {unassignedClientAlertInfo.isDismissed ? 'Toca para abrir alerta' : 'Alerta abierta'}
                  </div>
                </div>
              </button>
            )}

            {/* Alerta de Ticket Sin Nivel Asignado */}
            {unassignedLevelAlertInfo.hasNoLevel && (
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('akasia:support:reopen_unassigned_level_alert'));
                  setIsAlertsMenuOpen(false);
                }}
                className="w-100 text-start btn btn-sm d-flex align-items-center gap-2 px-2 py-2 rounded-2 border-0 shadow-none mb-1"
                style={{
                  backgroundColor: isDark ? 'rgba(99, 102, 241, 0.16)' : '#eef2ff',
                  color: isDark ? '#c7d2fe' : '#3730a3',
                  fontSize: '0.78rem'
                }}
              >
                <FontAwesomeIcon icon={faLayerGroup} style={{ color: '#6366f1', fontSize: '0.85rem' }} />
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-bold text-truncate">Asignar Nivel</div>
                  <div style={{ fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                    {unassignedLevelAlertInfo.isDismissed ? 'Toca para abrir alerta' : 'Alerta abierta'}
                  </div>
                </div>
              </button>
            )}

            {!policiesAlertInfo.hasExpired &&
              !unassignedClientAlertInfo.hasNoClient &&
              !unassignedLevelAlertInfo.hasNoLevel && (
                <div
                  className="px-2 py-2 text-center"
                  style={{ fontSize: '0.75rem', color: isDark ? '#64748b' : '#94a3b8' }}
                >
                  No hay alertas activas
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

TicketMobileSubHeader.propTypes = {
  ticket: PropTypes.object,
  onToggleMobileSidebar: PropTypes.func,
  onTicketUpdated: PropTypes.func
};

export default TicketMobileSubHeader;
